import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import axios from "axios";
import {API_URL} from "../../../../config";
import {normalizeWindowResponse} from "./rsoForecastWindowHelpers";
import {isSignInExpired} from "./RsoSignInExpired";

export const FORECAST_WINDOW_REFRESH_MS = 5 * 60 * 1000;
const windowEndpoint = `${API_URL}/Dashboard/api/RsoPropertyForecast/window`;
const withoutClock = (value) => (value ? JSON.stringify({...value, serverNow: null}) : "");
const sameWeek = (left, right) => !!left && !!right && withoutClock(left) === withoutClock(right);

// The RSO input week as NANCY reports it. `window` is null whenever the input week is
// switched off (or cannot be read), and every screen then behaves exactly as before.
// status: "off" (not requested), "loading" (first read), "ready" (read finished, even if it failed).
// overrideEnabled: sending outside the input week with a sign-off is allowed (false unless NANCY says true).
// signInExpired: the last read was refused because the sign-in expired (the last week is still kept).
export default function useRsoForecastWindow({token, enabled = true}) {
  const active = enabled && !!token;
  // version counts finished reads, so a screen can refresh its own counts on every focus or 5-minute refresh.
  const [state, setState] = useState({token: null, loaded: false, window: null, canViewTeamStatus: false, overrideEnabled: false, version: 0,
    signInExpired: false});
  const attempt = useRef(0);
  const alive = useRef(true);
  useEffect(() => {alive.current = true; return () => {alive.current = false;};}, []);
  const refresh = useCallback(async () => {
    if (!active) return;
    const current = ++attempt.current;
    try {
      const response = await axios.get(windowEndpoint, {headers: {Authorization: `Bearer ${token}`}, timeout: 30000});
      if (!alive.current || current !== attempt.current) return;
      const next = normalizeWindowResponse(response?.data);
      setState((previous) => {
        // Switched on but the forecasting service was unreachable: keep the week already on screen rather
        // than flipping back to the old screens mid-session. With no earlier week, behave as switched off.
        if (next.windowUnavailable && previous.window && previous.token === token) return {...previous, signInExpired: false, version: previous.version + 1};
        // Keep the same object while the week itself is unchanged, so screens don't reload every refresh.
        return {token, loaded: true, canViewTeamStatus: next.canViewTeamStatus, overrideEnabled: next.overrideEnabled, signInExpired: false,
          version: previous.version + 1, window: sameWeek(previous.window, next.window) ? previous.window : next.window};
      });
    } catch (failure) {
      // A failed refresh keeps the last known week; a failed first read behaves as switched off.
      if (alive.current && current === attempt.current) {
        const signInExpired = isSignInExpired(failure);
        setState((previous) => previous.loaded ? {...previous, token, signInExpired, version: previous.version + 1}
          : {token, loaded: true, window: null, canViewTeamStatus: false, overrideEnabled: false, signInExpired, version: previous.version + 1});
      }
    }
  }, [active, token]);
  useEffect(() => {
    if (!active) return undefined;
    refresh();
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    const timer = window.setInterval(refresh, FORECAST_WINDOW_REFRESH_MS);
    return () => {window.removeEventListener("focus", onFocus); window.clearInterval(timer);};
  }, [active, refresh]);
  const status = !active ? "off" : state.loaded ? "ready" : "loading";
  const entryWindow = active ? state.window : null;
  // Team access belongs to the signed-in user; never carry it across a sign-in change.
  const canViewTeamStatus = active && state.token === token && state.canViewTeamStatus;
  const overrideEnabled = !!entryWindow && state.token === token && state.overrideEnabled === true;
  const version = state.version;
  const signInExpired = active && state.token === token && state.signInExpired === true;
  return useMemo(() => ({status, window: entryWindow, canViewTeamStatus, overrideEnabled, refresh, version, signInExpired}),
    [status, entryWindow, canViewTeamStatus, overrideEnabled, refresh, version, signInExpired]);
}
